import {
  AlertTriangle,
  Circle,
  CircleCheck,
  ClipboardList,
  MinusCircle,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';

import { useAuthStore } from '../../../stores/auth-store';
import {
  useChecklistDetail,
  useFulfillChecklistItem,
  useMarkChecklistItemNotApplicable,
  useReopenChecklistItem,
} from '../hooks/use-checklist-detail';
import { useChecklists } from '../hooks/use-checklists';

import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

/**
 * ChecklistBlock — Fatia E / Frente A (conteúdo da seção "Checklist" no TabActions).
 *
 * Render REAL dos itens (antes stub só nome+progresso): sob o cabeçalho de cada
 * checklist (nome + progresso), lista os itens com estado (pendente/cumprido/
 * não-aplicável/falhou), marcador de CRÍTICO pendente, proveniência (IA×humano) e
 * AÇÕES INLINE nos pendentes (Cumprir / N/A com motivo ≥5 chars). Dados de
 * `GET /v1/checklists/:id` (itens) + `useChecklists` (resumo/WS). Aditivo: sem
 * checklist ⇒ empty-state honesto.
 */
export function ChecklistBlock({
  conversationId,
  disabled = false,
}: {
  conversationId: string;
  disabled?: boolean;
}): JSX.Element {
  const { checklists } = useChecklists(conversationId);

  if (checklists.length === 0) {
    return (
      <div className="empty-mini">
        <ClipboardList className="icon" size={14} aria-hidden />
        Sem checklist instanciada nesta conversa.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="checklist-block">
      {checklists.map((c) => (
        <div key={c.id}>
          {/* Cabeçalho do grupo em EYEBROW, fiel ao mockup 2a (`.pn__gname`):
              text-2xs, semibold, maiúsculas, tracking de caps, `--fg-3`. A
              versão anterior usava 12px/peso 500 em caixa normal e cor herdada —
              lia como título de conteúdo, não como rótulo de seção, e competia
              com o nome dos itens. É o mesmo idioma do eyebrow que o
              `PanelSection` já usa acima. */}
          <div className="flex items-baseline gap-2 mb-1">
            <span
              className="flex-1 truncate uppercase"
              style={{
                fontSize: 'var(--text-2xs)',
                fontWeight: 'var(--fw-semibold)',
                letterSpacing: 'var(--track-caps)',
                color: 'var(--fg-3)',
              }}
            >
              {c.name}
            </span>
            <span
              className="font-mono shrink-0"
              style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-4)' }}
            >
              {c.completedItems}/{c.totalItems}
            </span>
          </div>
          {/* Progresso do grupo (enxerto da opção 1d na 2a): custa 3px e responde
              «quanto falta» sem cobrar nada de cada linha. A ação em LOTE da 1d
              ficou fora de propósito — lote incide justamente nos itens críticos,
              que são os que bloqueiam o encerramento. */}
          <div
            data-testid="checklist-progresso"
            role="progressbar"
            aria-label={`Progresso de ${c.name}`}
            aria-valuenow={c.completedItems}
            aria-valuemin={0}
            aria-valuemax={c.totalItems}
            style={{
              height: 3,
              borderRadius: 'var(--radius-pill)',
              background: 'var(--bg-muted)',
              overflow: 'hidden',
              marginBottom: 8,
            }}
          >
            <span
              style={{
                display: 'block',
                height: '100%',
                width: `${c.totalItems > 0 ? Math.round((c.completedItems / c.totalItems) * 100) : 0}%`,
                background: 'var(--success-fg)',
              }}
            />
          </div>
          <ChecklistItems checklistId={c.id} disabled={disabled} />
        </div>
      ))}
    </div>
  );
}

function StatusIcon({ status }: { status: string }): JSX.Element {
  if (status === 'fulfilled') {
    return (
      <CircleCheck
        size={13}
        aria-label="cumprido"
        style={{ color: 'var(--success-fg)', flexShrink: 0 }}
      />
    );
  }
  if (status === 'not_applicable') {
    return (
      <MinusCircle
        size={13}
        aria-label="não aplicável"
        style={{ color: 'var(--fg-2)', flexShrink: 0 }}
      />
    );
  }
  if (status === 'failed') {
    return (
      <XCircle size={13} aria-label="falhou" style={{ color: 'var(--danger-fg)', flexShrink: 0 }} />
    );
  }
  return <Circle size={13} aria-label="pendente" style={{ color: 'var(--fg-3)', flexShrink: 0 }} />;
}

/**
 * Hora local HH:MM do cumprimento. Só a hora: a data já vive no contexto da
 * conversa, e o painel tem 360px.
 */
function horaDe(iso: string | null): string | null {
  if (iso === null) return null;
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

/**
 * Rastro de autoria — só no item JÁ CUMPRIDO (decisão do TL: no pendente a
 * autoria não existe e não pode custar um pixel).
 *
 * LIMITAÇÃO DECLARADA: o wire de `GET /v1/checklists/:id` traz
 * `fulfilledByUserId`, não o NOME. Então resolvemos apenas o caso que dá para
 * resolver sem endpoint novo — «você», pelo id do operador logado. Para outro
 * operador mostramos a hora e nada mais; o nome de terceiro exige campo no
 * engine (registrado como Item, não escondido aqui).
 */
function Autoria({
  item,
  meuUserId,
}: {
  item: {
    status: string;
    fulfilledAt: string | null;
    fulfilledByUserId: string | null;
    fulfilledViaAgent: boolean;
    notApplicableReason: string | null;
  };
  meuUserId: string | undefined;
}): JSX.Element | null {
  if (item.status !== 'fulfilled' && item.status !== 'not_applicable') return null;
  const hora = horaDe(item.fulfilledAt);
  const souEuAgora = item.fulfilledByUserId !== null && item.fulfilledByUserId === meuUserId;

  /**
   * No N/A, o que informa é o MOTIVO — não a hora. Mostrar os dois punha um
   * quarto elemento de largura fixa na linha e o nome quebrava em quatro
   * pedaços com o texto sobrepondo (visto no print de 13/Ago). Autor e hora
   * seguem no `title`, que é onde a informação secundária deve morar num painel
   * de 360px.
   */
  if (item.status === 'not_applicable') {
    const motivo = item.notApplicableReason ?? '';
    if (motivo.length === 0) return null;
    return (
      <span
        className="truncate shrink"
        style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-4)', maxWidth: 130 }}
        title={`Não se aplica — ${motivo}${hora !== null ? ` (${souEuAgora ? 'você' : 'outro operador'}, ${hora})` : ''}`}
      >
        {motivo}
      </span>
    );
  }

  if (item.fulfilledViaAgent) {
    return (
      <span
        className="flex items-center gap-1 shrink-0 font-mono"
        style={{ fontSize: 'var(--text-2xs)', color: 'var(--ai-fg)' }}
        title={hora !== null ? `Cumprido pela IA às ${hora}` : 'Cumprido pela IA'}
      >
        <Sparkles size={10} aria-hidden />
        <span aria-label="cumprido pela IA">IA</span>
        {hora !== null && <span>· {hora}</span>}
      </span>
    );
  }

  if (hora === null) return null;
  const souEu = item.fulfilledByUserId !== null && item.fulfilledByUserId === meuUserId;
  return (
    <span
      className="shrink-0 font-mono"
      style={{ fontSize: 'var(--text-2xs)', color: 'var(--fg-4)' }}
      title={souEu ? `Você cumpriu às ${hora}` : `Cumprido às ${hora}`}
    >
      {souEu ? `você · ${hora}` : hora}
    </span>
  );
}

/**
 * Espelho CLIENTE da janela de arrependimento do engine (60s). Serve só para
 * decidir QUAL afordância mostrar — «desfazer» seco ou «reabrir» com motivo.
 * A autoridade é do backend: se o relógio do cliente divergir, o 400 volta com
 * a mensagem certa e o toast aparece. A tela não decide, ela só não mente.
 */
const JANELA_DESFAZER_MS = 60_000;

function ehEscorregaoDeClique(
  item: {
    isCritical: boolean;
    fulfilledAt: string | null;
    fulfilledByUserId: string | null;
    fulfilledViaAgent: boolean;
  },
  meuUserId: string | undefined,
): boolean {
  if (item.isCritical || item.fulfilledViaAgent) return false;
  if (item.fulfilledAt === null || item.fulfilledByUserId === null) return false;
  if (meuUserId === undefined || item.fulfilledByUserId !== meuUserId) return false;
  return Date.now() - new Date(item.fulfilledAt).getTime() <= JANELA_DESFAZER_MS;
}

function ChecklistItems({
  checklistId,
  disabled,
}: {
  checklistId: string;
  disabled: boolean;
}): JSX.Element | null {
  const { data } = useChecklistDetail(checklistId);
  const fulfill = useFulfillChecklistItem();
  const markNa = useMarkChecklistItemNotApplicable();
  const reopen = useReopenChecklistItem();
  const meuUserId = useAuthStore((s) => s.user?.id);
  const [naItemId, setNaItemId] = useState<string | null>(null);
  const [naReason, setNaReason] = useState('');
  /** Item cuja REABERTURA está pedindo motivo (crítico, da IA, fora da janela). */
  const [reopenItemId, setReopenItemId] = useState<string | null>(null);
  const [reopenReason, setReopenReason] = useState('');

  if (!data) return null;

  const closeNa = (): void => {
    setNaItemId(null);
    setNaReason('');
  };

  return (
    <ul className="flex flex-col">
      {data.items.map((item) => {
        const pendente = item.status === 'pending';
        const acionavel = pendente && !disabled;
        const muted = item.status === 'fulfilled' || item.status === 'not_applicable';
        const editandoNa = naItemId === item.id;
        const editandoReopen = reopenItemId === item.id;
        // Item que saiu de pendente pode voltar — é a única porta, e ela existe
        // porque antes do dia 13/Ago um clique errado era irreversível.
        const reabrivel = !pendente && !disabled && item.status !== 'failed';
        const desfazerSeco = reabrivel && ehEscorregaoDeClique(item, meuUserId);

        /**
         * A política da opção 2a: a ação se REVELA no hover/foco, exceto no item
         * CRÍTICO, onde fica FIXA. Crítico pendente bloqueia o encerramento em
         * Regulated — a ação dele não pode depender de o operador passar o mouse.
         * O atributo declara a regra; o CSS apenas a obedece (e o teste lê o
         * atributo, porque jsdom não tem hover).
         */
        const politica = item.isCritical ? 'fixa' : 'revelada';

        return (
          <li
            key={item.id}
            className={`checklist-linha text-xs ${item.isCritical ? 'checklist-linha--critica' : ''}`}
            data-critico={item.isCritical ? 'true' : undefined}
            {...(acionavel && !editandoNa
              ? {
                  tabIndex: 0,
                  onKeyDown: (e: ReactKeyboardEvent<HTMLLIElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      fulfill.mutate({ itemId: item.id });
                    } else if (e.key === 'n' || e.key === 'N') {
                      e.preventDefault();
                      setNaItemId(item.id);
                      setNaReason('');
                    }
                  },
                }
              : {})}
          >
            <div className="flex items-center gap-2" style={{ minHeight: 26 }}>
              {/* Crítico pendente usa o triângulo COMO marcador de estado, não
                  além dele — é o que o mockup 2a desenhou. Círculo + triângulo
                  gastava 19px de largura exatamente nas linhas que também
                  carregam a ação fixa, e o nome quebrava em duas (medido: 32px
                  em vez de 26px por linha). «Pendente e crítico» é um estado,
                  não dois. */}
              {item.isCritical && pendente ? (
                <AlertTriangle
                  size={13}
                  aria-label="pendente e crítico"
                  style={{ color: 'var(--danger-fg)', flexShrink: 0 }}
                />
              ) : (
                <StatusIcon status={item.status} />
              )}
              {/* Pendente você LÊ — o nome quebra e aparece inteiro, porque é
                  instrução do que fazer. Registro você VARRE — o nome trunca em
                  uma linha, com o texto completo no `title`. Sem essa distinção,
                  um item N/A de nome longo ocupava 64px medidos, quase o triplo
                  de uma linha, para dizer algo que já aconteceu. */}
              <span
                className={`flex-1 min-w-0 ${pendente ? '' : 'truncate'}`}
                title={pendente ? undefined : item.name}
                style={{
                  // Token longo sem espaço («encaminhamento/pendência») TRANSBORDA
                  // a caixa em vez de quebrar, e passa por baixo do que vem à
                  // direita — visto no print de 13/Ago, com o motivo do N/A
                  // sobrepondo o nome riscado. `min-w-0` sozinho não resolve:
                  // ele deixa o flex encolher a CAIXA, não o texto.
                  overflowWrap: 'anywhere',
                  ...(muted ? { color: 'var(--fg-3)' } : {}),
                  ...(item.status === 'not_applicable' ? { textDecoration: 'line-through' } : {}),
                }}
              >
                {item.name}
              </span>
              <Autoria item={item} meuUserId={meuUserId} />
              {reabrivel && !editandoReopen && (
                <span className="checklist-acao shrink-0" data-acao="revelada">
                  <button
                    type="button"
                    className="checklist-desfazer"
                    aria-label={
                      desfazerSeco ? `Desfazer: ${item.name}` : `Reabrir com motivo: ${item.name}`
                    }
                    title={
                      desfazerSeco ? 'Desfazer (você acabou de marcar)' : 'Reabrir — exige motivo'
                    }
                    onClick={() => {
                      if (desfazerSeco) {
                        reopen.mutate({ itemId: item.id });
                      } else {
                        setReopenItemId(item.id);
                        setReopenReason('');
                      }
                    }}
                  >
                    {desfazerSeco ? 'desfazer' : 'reabrir'}
                  </button>
                </span>
              )}
              {acionavel && !editandoNa && (
                <span
                  className="checklist-acao flex items-center gap-0.5 shrink-0"
                  data-acao={politica}
                >
                  <button
                    type="button"
                    className="checklist-alvo"
                    title="Cumprir (Enter)"
                    aria-label={`Cumprir: ${item.name}`}
                    disabled={fulfill.isPending}
                    onClick={() => fulfill.mutate({ itemId: item.id })}
                  >
                    <CircleCheck size={13} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="checklist-alvo"
                    title="Não se aplica (N)"
                    aria-label={`Marcar não se aplica: ${item.name}`}
                    onClick={() => {
                      setNaItemId(item.id);
                      setNaReason('');
                    }}
                  >
                    <MinusCircle size={13} aria-hidden />
                  </button>
                </span>
              )}
            </div>

            {editandoReopen && (
              <div className="flex items-center gap-2 mb-1 ml-6">
                <input
                  type="text"
                  value={reopenReason}
                  onChange={(e) => setReopenReason(e.target.value)}
                  placeholder="motivo da reabertura (≥5 caracteres)"
                  className="flex-1 min-w-0 rounded border px-1.5 py-0.5 text-xs"
                  style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--xs"
                  disabled={reopenReason.trim().length < 5 || reopen.isPending}
                  onClick={() => {
                    reopen.mutate(
                      { itemId: item.id, reason: reopenReason.trim() },
                      {
                        onSuccess: () => {
                          setReopenItemId(null);
                          setReopenReason('');
                        },
                      },
                    );
                  }}
                >
                  Reabrir
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  onClick={() => {
                    setReopenItemId(null);
                    setReopenReason('');
                  }}
                >
                  Cancelar
                </button>
              </div>
            )}

            {/* O motivo do N/A é o único caso que ganha uma segunda linha — e só
                enquanto está sendo escrito (≥5 caracteres, regra do engine). */}
            {editandoNa && (
              <div className="flex items-center gap-2 mb-1 ml-6">
                <input
                  type="text"
                  value={naReason}
                  onChange={(e) => setNaReason(e.target.value)}
                  placeholder="motivo (≥5 caracteres)"
                  className="flex-1 min-w-0 rounded border px-1.5 py-0.5 text-xs"
                  style={{ borderColor: 'var(--border-default)', background: 'var(--bg-surface)' }}
                />
                <button
                  type="button"
                  className="btn btn--primary btn--xs"
                  disabled={naReason.trim().length < 5 || markNa.isPending}
                  onClick={() => {
                    markNa.mutate(
                      { itemId: item.id, reason: naReason.trim() },
                      { onSuccess: closeNa },
                    );
                  }}
                >
                  Confirmar N/A
                </button>
                <button type="button" className="btn btn--ghost btn--xs" onClick={closeNa}>
                  Cancelar
                </button>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
